import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Tooltip,
  IconButton,
  alpha,
} from '@mui/material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import axiosInstance from '../../../utils/axios';
import {
  Edit as EditIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CheckCircle as ApprovedIcon,
  HourglassEmpty as PendingIcon,
  Cancel as RejectedIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  ShoppingCart as ShoppingIcon,
  Payment as PaymentIcon,
  Schedule as ScheduleIcon,
  OpenInFull as ExpandIcon,
  CloseFullscreen as CollapseIcon,
  OpenInNew as OpenInNewIcon,
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Circle as CircleIcon,
  Tag as TagIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmRejectDialog from '../../common/ConfirmRejectDialog';
import { canEditClaim, canRejectClaim } from '../../../utils/financialPermissions';
import { resolveUserId, userDetailsPath } from '../../../utils/userDetailsLink';
import ClaimStripePay from './ClaimStripePay';
import { injectDetailsPageStyles } from '../../common/DetailsPagePrimitives';
import {
  fetchClaimReceiptPreviewBlob,
  claimHasPurchaseReceipt,
  claimHasReimbursementReceipt,
  receiptPathIsPdf,
} from '../../../utils/protectedFilePreview';
import { signedPreviewUrlIsUsable } from '../../../utils/storageUrl';
import SystemInformationSection from '../../common/SystemInformationSection';
import { formatSystemDateTime } from '../../../utils/systemDateFormat';

// ─── Design tokens ────────────────────────────────────────────────
const NEON       = '#38BDF8';
const NEON2      = '#818CF8';
const NEON3      = '#34D399';
const AMBER      = '#FBBF24';
const CORAL      = '#F87171';
const SURFACE_MID = '#0a1628';
const SURFACE_UP  = '#0f1f38';
const SURFACE_TOP = '#152640';
const TEXT_BODY   = '#E2E8F0';
const TEXT_MUTED  = '#64748B';
const TEXT_SUB    = '#94A3B8';

// ─── Helpers ──────────────────────────────────────────────────────
const formatDateSafe = (v, fmt) => {
  if (v == null || v === '') return '—';
  try { return format(new Date(v), fmt); } catch { return '—'; }
};

const formatMethod = (m) => {
  if (!m) return null;
  return String(m).split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const formatAuditUser = (u) => {
  if (!u) return null;
  if (typeof u === 'string') return u;
  return u?.name || null;
};


const STATUS_CONFIG = {
  pending:  { color: NEON2, label: 'Pending',  icon: PendingIcon  },
  on_hold:  { color: AMBER, label: 'On hold',  icon: PendingIcon  },
  approved: { color: NEON3, label: 'Approved', icon: ApprovedIcon },
  rejected: { color: CORAL, label: 'Rejected', icon: RejectedIcon },
};

// ─── Sub-components ───────────────────────────────────────────────

/** Animated section wrapper */
const Section = ({ title, icon: Icon, accent = NEON, delay = 0, stretch = false, children }) => (
  <Box sx={{
    borderRadius: '16px',
    border: `1px solid ${alpha(accent, 0.14)}`,
    background: `linear-gradient(160deg, ${alpha(SURFACE_UP, 0.75)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
    overflow: 'hidden',
    animation: `fadeUp 0.4s ease ${delay}s both`,
    // When stretch=true the section fills its grid cell height and its inner
    // content box grows to consume remaining space (used for the receipt columns)
    ...(stretch && { display: 'flex', flexDirection: 'column', height: '100%' }),
    '@keyframes fadeUp': {
      from: { opacity: 0, transform: 'translateY(10px)' },
      to:   { opacity: 1, transform: 'translateY(0)' },
    },
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.2,
      px: 2.5, py: 1.5,
      borderBottom: `1px solid ${alpha(accent, 0.1)}`,
      background: alpha(accent, 0.04),
      flexShrink: 0,
    }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: '7px',
        background: alpha(accent, 0.12), border: `1px solid ${alpha(accent, 0.22)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 13, color: accent }} />
      </Box>
      <Typography sx={{
        fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
        fontSize: '0.78rem', color: alpha(TEXT_BODY, 0.85),
        letterSpacing: '0.07em', textTransform: 'uppercase',
      }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 2, ...(stretch && { flex: 1, display: 'flex', flexDirection: 'column' }) }}>
      {children}
    </Box>
  </Box>
);

/** Compact info row */
const InfoRow = ({ icon: Icon, label, value, emptyLabel, color = NEON, onClick }) => {
  const display = value || emptyLabel;
  if (!display) return null;
  const isEmpty = !value && emptyLabel;
  const isInteractive = Boolean(onClick && !isEmpty);
  return (
    <Box
      role={isInteractive ? 'link' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={isInteractive ? onClick : undefined}
      onKeyDown={isInteractive ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); }
      } : undefined}
      sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, py: 0.9, px: 1.2, borderRadius: '9px',
      cursor: isInteractive ? 'pointer' : 'default',
      transition: 'background 0.15s',
      '&:hover': isInteractive ? { background: alpha(color, 0.06) } : {},
    }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
        background: alpha(isEmpty ? TEXT_MUTED : color, 0.1),
        border: `1px solid ${alpha(isEmpty ? TEXT_MUTED : color, 0.18)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 14, color: isEmpty ? TEXT_MUTED : alpha(color, 0.85) }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.65rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: '0.88rem', fontFamily: "'DM Sans', sans-serif", fontWeight: isEmpty ? 400 : 500,
          color: isEmpty ? TEXT_MUTED : (isInteractive ? color : TEXT_BODY), fontStyle: isEmpty ? 'italic' : 'normal',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {display}
        </Typography>
      </Box>
    </Box>
  );
};

/** Metric card */
const MetricCard = ({ label, value, sub, icon: Icon, color = NEON, sx = {}, onClick }) => (
  <Box
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(e);
      }
    } : undefined}
    sx={{
    position: 'relative', borderRadius: '14px', px: 2.5, py: 2,
    background: `linear-gradient(145deg, ${alpha(color, 0.09)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)`,
    border: `1px solid ${alpha(color, 0.2)}`,
    boxShadow: `0 0 24px ${alpha(color, 0.07)}, inset 0 1px 0 ${alpha(color, 0.1)}`,
    overflow: 'hidden',
    cursor: onClick ? 'pointer' : 'default',
    transition: 'box-shadow 0.25s, border-color 0.25s, transform 0.2s ease',
    '&:hover': {
      boxShadow: `0 0 36px ${alpha(color, 0.16)}`,
      borderColor: alpha(color, 0.35),
      ...(onClick ? { transform: 'translateY(-1px)' } : {}),
    },
    ...sx,
  }}>
    <Box sx={{ position: 'absolute', top: -16, right: -16, width: 70, height: 70, borderRadius: '50%', background: alpha(color, 0.08), filter: 'blur(18px)', pointerEvents: 'none' }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
      {Icon && <Icon sx={{ fontSize: 13, color: alpha(color, 0.75) }} />}
      <Typography sx={{ fontSize: '0.67rem', fontFamily: "'DM Sans', sans-serif", color: TEXT_MUTED, letterSpacing: '0.09em', textTransform: 'uppercase', fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
    <Typography className="metric-card-value" sx={{
      fontSize: '1.25rem',
      fontWeight: 700,
      fontFamily: "'Rajdhani', sans-serif",
      color,
      letterSpacing: 0.3,
      lineHeight: 1.15,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      ...(onClick ? { textDecoration: 'underline', textDecorationColor: alpha(color, 0.35), textUnderlineOffset: 3 } : {}),
    }}>
      {value || '—'}
    </Typography>
    {sub && <Typography sx={{ fontSize: '0.7rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", mt: 0.4 }}>{sub}</Typography>}
  </Box>
);

/** Flag badge: Claimable / Paid */
const FlagBadge = ({ label, active, activeColor = NEON3 }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 0.7,
    px: 1.2, py: 0.4, borderRadius: '20px',
    background: alpha(active ? activeColor : TEXT_MUTED, 0.1),
    border: `1px solid ${alpha(active ? activeColor : TEXT_MUTED, 0.25)}`,
  }}>
    <CircleIcon sx={{ fontSize: 6, color: active ? activeColor : TEXT_MUTED }} />
    <Typography sx={{ fontSize: '0.72rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: active ? activeColor : TEXT_MUTED, letterSpacing: '0.04em' }}>
      {label}
    </Typography>
  </Box>
);

/** Document viewer
 *  - Compact by default: viewer body fills whatever height the Section has (flex-grow)
 *  - Expand button opens a fixed full-viewport overlay for zoomed reading
 */
const DocumentViewer = ({ title, receiptLabel, receiptUrl, isPdf = false, accent = NEON, delay = 0 }) => {
  const [zoomed, setZoomed]             = useState(false);   // full-screen overlay
  const [viewerError, setViewerError]   = useState(false);
  const [viewerLoaded, setViewerLoaded] = useState(false);
  const iframeRef = useRef(null);
  const imageRef  = useRef(null);

  useEffect(() => {
    setViewerError(false);
    setViewerLoaded(false);
  }, [receiptUrl]);

  const handleRefresh = () => {
    setViewerError(false); setViewerLoaded(false);
    if (isPdf && iframeRef.current) {
      const s = iframeRef.current.src; iframeRef.current.src = '';
      setTimeout(() => { if (iframeRef.current) iframeRef.current.src = s; }, 50);
    }
    if (!isPdf && imageRef.current) {
      const s = imageRef.current.src; imageRef.current.src = '';
      setTimeout(() => { if (imageRef.current) imageRef.current.src = s; }, 50);
    }
  };

  // The actual document iframe / img — reused in both inline & overlay
  const DocContent = ({ overlayMode = false }) => (
    <>
      {!viewerLoaded && !viewerError && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, background: '#0a0e1a' }}>
          <Box sx={{ width: 44, height: 44, borderRadius: '10px', background: alpha(accent, 0.1), border: `1px solid ${alpha(accent, 0.2)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PdfIcon sx={{ fontSize: 20, color: accent }} />
          </Box>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: TEXT_MUTED }}>Loading document...</Typography>
        </Box>
      )}
      {viewerError && (
        <Box sx={{ position: 'absolute', inset: 0, zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: '#0a0e1a' }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '12px', background: alpha(CORAL, 0.1), border: `1px solid ${alpha(CORAL, 0.25)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ErrorIcon sx={{ fontSize: 24, color: CORAL }} />
          </Box>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.88rem', color: TEXT_BODY }}>Preview unavailable</Typography>
        </Box>
      )}
      {receiptUrl && (isPdf ? (
        <iframe ref={overlayMode ? null : iframeRef}
          src={`${receiptUrl}#toolbar=0&navpanes=0&view=FitH`}
          style={{ width: '100%', height: '100%', border: 'none', opacity: viewerLoaded && !viewerError ? 1 : 0, transition: 'opacity 0.3s' }}
          title={receiptLabel} onLoad={() => setViewerLoaded(true)} onError={() => setViewerError(true)}
        />
      ) : (
        <Box ref={overlayMode ? null : imageRef} component="img" src={receiptUrl} alt={receiptLabel}
          sx={{ width: '100%', height: '100%', objectFit: 'contain', opacity: viewerLoaded && !viewerError ? 1 : 0, transition: 'opacity 0.3s' }}
          onLoad={() => setViewerLoaded(true)} onError={() => setViewerError(true)}
        />
      ))}
    </>
  );

  return (
    <>
      {/* ── Inline compact viewer (fills row height via stretch Section) ── */}
      <Section title={title} icon={PdfIcon} accent={accent} delay={delay} stretch>
        {/* The inner Box must also stretch to fill Section's flex content area */}
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 0 }}>

          {/* Toolbar */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 2, py: 1.2, borderRadius: '10px 10px 0 0',
            background: alpha(SURFACE_UP, 0.9),
            border: `1px solid ${alpha(accent, 0.14)}`, borderBottom: 'none',
            flexShrink: 0,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0, flex: 1 }}>
              <Box sx={{
                width: 28, height: 28, borderRadius: '7px', flexShrink: 0,
                background: alpha(isPdf ? CORAL : accent, 0.15),
                border: `1px solid ${alpha(isPdf ? CORAL : accent, 0.25)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {isPdf ? <PdfIcon sx={{ fontSize: 14, color: CORAL }} /> : <ReceiptIcon sx={{ fontSize: 14, color: accent }} />}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', fontWeight: 600, color: TEXT_BODY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: { xs: 140, sm: 220 } }}>
                  {receiptLabel}
                </Typography>
                <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.6rem', color: TEXT_MUTED }}>
                  {isPdf ? 'PDF Document' : 'Image Document'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.4, flexShrink: 0 }}>
              {[
                { tip: 'Refresh',          Icon: RefreshIcon,   fn: handleRefresh },
                { tip: 'Open in new tab',  Icon: OpenInNewIcon, fn: () => window.open(receiptUrl, '_blank') },
                { tip: 'Zoom / Expand',    Icon: ExpandIcon,    fn: () => setZoomed(true) },
              ].map(({ tip, Icon, fn }) => (
                <Tooltip key={tip} title={tip} placement="top">
                  <IconButton onClick={fn} size="small" sx={{ color: TEXT_MUTED, borderRadius: '7px', '&:hover': { background: alpha(accent, 0.08), color: accent } }}>
                    <Icon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
          </Box>

          {/* Viewer body — flex: 1 so it fills remaining height of the row */}
          <Box sx={{
            position: 'relative',
            flex: 1,
            minHeight: 180,   // minimum so it never collapses to 0
            overflow: 'hidden',
            border: `1px solid ${alpha(accent, 0.14)}`,
            background: '#0a0e1a',
          }}>
            <DocContent />
          </Box>

          {/* Status bar */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 2, py: 0.8, flexShrink: 0,
            background: alpha(SURFACE_MID, 0.6),
            border: `1px solid ${alpha(accent, 0.08)}`,
            borderTop: `1px solid ${alpha(accent, 0.06)}`,
            borderRadius: '0 0 8px 8px',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: viewerLoaded && !viewerError ? NEON3 : viewerError ? CORAL : NEON2, boxShadow: `0 0 5px ${viewerLoaded && !viewerError ? NEON3 : viewerError ? CORAL : NEON2}` }} />
              <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.6rem', color: TEXT_MUTED }}>
                {viewerLoaded && !viewerError ? 'Ready' : viewerError ? 'Load error' : 'Loading...'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.6rem', color: alpha(TEXT_MUTED, 0.5) }}>
                {isPdf ? 'PDF' : 'Image'} · Inline viewer
              </Typography>
              <Tooltip title="Zoom / Expand" placement="top">
                <IconButton onClick={() => setZoomed(true)} size="small"
                  sx={{ color: alpha(accent, 0.5), borderRadius: '5px', p: 0.3, '&:hover': { color: accent, background: alpha(accent, 0.08) } }}>
                  <ExpandIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Section>

      {/* ── Zoom overlay ── */}
      {zoomed && (
        <Box onClick={() => setZoomed(false)} sx={{
          position: 'fixed', inset: 0, zIndex: 1400,
          background: 'rgba(4, 9, 20, 0.92)',
          backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'fadeIn 0.18s ease both',
          '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
        }}>
          <Box onClick={e => e.stopPropagation()} sx={{
            position: 'relative',
            width: { xs: '96vw', md: '86vw' },
            height: { xs: '88vh', md: '90vh' },
            borderRadius: '16px',
            border: `1px solid ${alpha(accent, 0.3)}`,
            overflow: 'hidden',
            background: '#0a0e1a',
            boxShadow: `0 0 80px ${alpha(accent, 0.18)}`,
            display: 'flex', flexDirection: 'column',
          }}>
            {/* Overlay toolbar */}
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: 2.5, py: 1.2, flexShrink: 0,
              background: alpha(SURFACE_UP, 0.95),
              borderBottom: `1px solid ${alpha(accent, 0.14)}`,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <Box sx={{ width: 28, height: 28, borderRadius: '7px', background: alpha(accent, 0.12), border: `1px solid ${alpha(accent, 0.22)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {isPdf ? <PdfIcon sx={{ fontSize: 14, color: CORAL }} /> : <ReceiptIcon sx={{ fontSize: 14, color: accent }} />}
                </Box>
                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', fontWeight: 600, color: TEXT_BODY }}>{receiptLabel}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Open in new tab" placement="top">
                  <IconButton onClick={() => window.open(receiptUrl, '_blank')} size="small" sx={{ color: TEXT_MUTED, borderRadius: '7px', '&:hover': { background: alpha(accent, 0.08), color: accent } }}>
                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close" placement="top">
                  <IconButton onClick={() => setZoomed(false)} size="small" sx={{ color: TEXT_MUTED, borderRadius: '7px', '&:hover': { background: alpha(CORAL, 0.1), color: CORAL } }}>
                    <CollapseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            {/* Overlay content */}
            <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
              <DocContent overlayMode />
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

/** Empty receipt placeholder — stretches to match the info column height */
const EmptyReceiptPlaceholder = ({ title, delay = 0, accent = NEON }) => (
  <Section title={title} icon={PdfIcon} accent={accent} delay={delay} stretch>
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, minHeight: 180, border: `1px dashed ${alpha(accent, 0.18)}`, borderRadius: '10px', background: alpha(SURFACE_UP, 0.3) }}>
      <PdfIcon sx={{ fontSize: 30, color: alpha(TEXT_MUTED, 0.3) }} />
      <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.84rem', color: TEXT_MUTED, fontStyle: 'italic' }}>No receipt uploaded</Typography>
    </Box>
  </Section>
);

// ─── Main Component ───────────────────────────────────────────────
const ClaimDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();

  const [claim, setClaim]                   = useState(null);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectError, setRejectError] = useState(null);
  const [purchaseReceiptUrl, setPurchaseReceiptUrl] = useState(null);
  const [reimbursementReceiptUrl, setReimbursementReceiptUrl] = useState(null);
  const [purchaseReceiptIsPdf, setPurchaseReceiptIsPdf] = useState(false);
  const [reimbursementReceiptIsPdf, setReimbursementReceiptIsPdf] = useState(false);

  const canEdit = claim ? canEditClaim(currentUser, claim) : false;
  const canReject = claim ? canRejectClaim(currentUser, claim) : false;

  useEffect(() => { injectDetailsPageStyles(); }, []);
  useEffect(() => { if (id && currentUser) fetchClaimDetails(); }, [id, currentUser]);

  useEffect(() => {
    if (searchParams.get('stripe') === 'cancelled') {
      setError('Stripe checkout was cancelled.');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!claim || !id) {
      setPurchaseReceiptUrl(null);
      setReimbursementReceiptUrl(null);
      setPurchaseReceiptIsPdf(false);
      setReimbursementReceiptIsPdf(false);
      return undefined;
    }

    let cancelled = false;
    let purchaseBlob = null;
    let reimbursementBlob = null;

    const loadReceipt = async (type, previewKey, hasReceipt, setUrl, setIsPdf) => {
      if (!hasReceipt) {
        setUrl(null);
        setIsPdf(false);
        return null;
      }

      const pathIsPdf = receiptPathIsPdf(claim, type);
      const signedUsable = claim[previewKey] && signedPreviewUrlIsUsable(claim[previewKey]);
      if (signedUsable) {
        if (!cancelled) {
          setUrl(claim[previewKey]);
          setIsPdf(pathIsPdf);
        }
        return null;
      }

      try {
        const preview = await fetchClaimReceiptPreviewBlob(id, type);
        if (!cancelled) {
          setUrl(preview.url);
          setIsPdf(preview.isPdf || pathIsPdf);
        } else {
          URL.revokeObjectURL(preview.url);
        }
        return preview.url;
      } catch {
        if (!cancelled) {
          setUrl(null);
          setIsPdf(false);
        }
        return null;
      }
    };

    (async () => {
      purchaseBlob = await loadReceipt(
        'purchase',
        'purchase_receipt_preview_url',
        claimHasPurchaseReceipt(claim),
        setPurchaseReceiptUrl,
        setPurchaseReceiptIsPdf,
      );
      if (cancelled) return;
      reimbursementBlob = await loadReceipt(
        'reimbursement',
        'reimbursement_receipt_preview_url',
        claimHasReimbursementReceipt(claim),
        setReimbursementReceiptUrl,
        setReimbursementReceiptIsPdf,
      );
    })();

    return () => {
      cancelled = true;
      if (purchaseBlob) URL.revokeObjectURL(purchaseBlob);
      if (reimbursementBlob) URL.revokeObjectURL(reimbursementBlob);
    };
  }, [
    claim,
    id,
    claim?.purchase_receipt_preview_url,
    claim?.reimbursement_receipt_preview_url,
    claim?.purchase_receipt_path,
    claim?.reimbursement_receipt_path,
  ]);

  const fetchClaimDetails = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/claims/${id}`);
      setClaim(response.data?.data ?? response.data);
      setError(null);
    } catch {
      setError('Failed to load claim details');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectClaim = async (reason) => {
    try {
      setRejectLoading(true);
      setRejectError(null);
      await axiosInstance.put(`/claims/${id}/reject`, { rejection_reason: reason });
      setRejectDialogOpen(false);
      setSuccessMessage('Claim rejected successfully.');
      await fetchClaimDetails();
    } catch (err) {
      const msg =
        err?.response?.data?.message
        || Object.values(err?.response?.data?.errors || {}).flat()?.[0]
        || 'Failed to reject claim.';
      setRejectError(msg);
      throw err;
    } finally {
      setRejectLoading(false);
    }
  };

  if (!currentUser || loading) return <Loading message="Loading claim details" size={56} variant="primary" />;
  if (!claim) return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <FeedbackMessage message={error || 'Claim not found'} severity="error" />
    </Container>
  );

  const statusCfg = STATUS_CONFIG[claim.status] ?? { color: NEON, label: claim.status, icon: ReceiptIcon };
  const claimant = claim.user || claim.record_creator || claim.creator;
  const claimantId = resolveUserId(claimant ?? claim.created_by);
  const claimantName = claimant?.name || claim.full_name;

  const hasPurchaseReceipt = claimHasPurchaseReceipt(claim);
  const hasReimbursementReceipt = claimHasReimbursementReceipt(claim);

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 8 }}>

      {/* ── Header ── */}
      <Header
        title="Claim Details"
        subtitle="View claim information"
        icon={ReceiptIcon}
        showBackButton
        onBackClick={() => navigate('/claims')}
        actionButton={
          (canEdit || canReject) && (
            <Box sx={{ display: 'flex', gap: 1.2 }}>
              {canEdit && (
                <Button startIcon={<EditIcon sx={{ fontSize: 15 }} />} onClick={() => navigate(`/claims/edit/${id}`)}
                  sx={{ borderRadius: '10px', px: 2.2, py: 0.65, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.83rem', background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.55)}`, color: NEON, transition: 'all 0.2s', '&:hover': { background: alpha(NEON, 0.08), borderColor: NEON, boxShadow: `0 0 14px ${alpha(NEON, 0.2)}` } }}>
                  Edit
                </Button>
              )}
              {canReject && (
                <Button startIcon={<RejectedIcon sx={{ fontSize: 15 }} />} onClick={() => { setRejectError(null); setRejectDialogOpen(true); }}
                  sx={{ borderRadius: '10px', px: 2.2, py: 0.65, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.83rem', background: 'transparent', border: `1.5px solid ${alpha(CORAL, 0.55)}`, color: CORAL, transition: 'all 0.2s', '&:hover': { background: alpha(CORAL, 0.08), borderColor: CORAL, boxShadow: `0 0 14px ${alpha(CORAL, 0.2)}` } }}>
                  Reject
                </Button>
              )}
            </Box>
          )
        }
      />

      {/* ── Feedback ── */}
      {error && <FeedbackMessage message={error} severity="error" onClose={() => setError(null)} sx={{ mb: 2.5, fontFamily: "'DM Sans', sans-serif", background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5' }} />}
      {successMessage && <FeedbackMessage message={successMessage} severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2.5, fontFamily: "'DM Sans', sans-serif", background: alpha(NEON3, 0.08), border: `1px solid ${alpha(NEON3, 0.3)}`, color: NEON3 }} />}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* ══════════════════════════════════════════
            HERO CARD  (unchanged)
            ══════════════════════════════════════════ */}
        <Box sx={{
          borderRadius: '16px',
          border: `1px solid ${alpha(statusCfg.color, 0.2)}`,
          background: `linear-gradient(135deg, ${alpha(SURFACE_UP, 0.9)} 0%, ${alpha(SURFACE_MID, 0.98)} 100%)`,
          p: { xs: 2, md: 2.5 },
          animation: 'fadeUp 0.35s ease both',
          '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(10px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
        }}>
          {/* Identity row */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: '9px', background: alpha(NEON, 0.1), border: `1px solid ${alpha(NEON, 0.2)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ReceiptIcon sx={{ fontSize: 16, color: NEON }} />
                </Box>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: TEXT_BODY, letterSpacing: 0.3, lineHeight: 1.15 }}>
                  {claim.item_bought || 'Claim'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 0.5 }}>
                {claim.receipt_number && (
                  <Typography sx={{ fontSize: '0.78rem', color: TEXT_MUTED, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.05em' }}>
                    #{claim.receipt_number}
                  </Typography>
                )}
                {claim.full_name && (
                  <Typography sx={{ fontSize: '0.78rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>
                    · {claim.full_name}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Status pill */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, px: 1.4, py: 0.5, borderRadius: '20px', background: alpha(statusCfg.color, 0.1), border: `1px solid ${alpha(statusCfg.color, 0.3)}`, alignSelf: 'flex-start' }}>
              <CircleIcon sx={{ fontSize: 7, color: statusCfg.color }} />
              <Typography sx={{ fontSize: '0.75rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: statusCfg.color, letterSpacing: '0.05em' }}>
                {statusCfg.label}
              </Typography>
            </Box>
          </Box>

          {/* Metric strip */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr 1fr',
              md: (claim.budget?.item || claim.budget_id)
                ? 'repeat(4, minmax(0, 1fr))'
                : 'repeat(3, minmax(0, 1fr))',
            },
            gap: 1.5,
            mb: 2,
          }}>
            <MetricCard label="Claim Amount" value={`RM ${parseFloat(claim.amount || 0).toFixed(2)}`} icon={MoneyIcon} color={NEON2} sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }} />
            <MetricCard label="Department" value={claim.department ? claim.department.charAt(0).toUpperCase() + claim.department.slice(1) : null} icon={BusinessIcon} color={NEON} />
            <MetricCard label="Purchase Date" value={formatDateSafe(claim.purchase_payment_date || claim.payment_date, 'dd MMM yyyy')} icon={CalendarIcon} color={NEON3} />
            {(claim.budget?.item || claim.budget_id) && (
              <MetricCard
                label="Budget"
                value={claim.budget?.item || `Budget #${claim.budget_id}`}
                icon={AccountBalanceIcon}
                color={AMBER}
                onClick={() => navigate(`/budgets/${claim.budget_id || claim.budget?.id}`)}
              />
            )}
          </Box>

        </Box>

        {/* ══════════════════════════════════════════
            ROW 2 — CLAIM DETAILS (full width, 1 row)
            Title + Description
            ══════════════════════════════════════════ */}
        {(Array.isArray(claim.line_items) && claim.line_items.length > 0) || claim.description ? (
        <Section title="Claim Details" icon={DescriptionIcon} accent={NEON} delay={0.08}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
            {Array.isArray(claim.line_items) && claim.line_items.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: '0.65rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.8, px: 1.2 }}>
                  Items purchased
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {claim.line_items.map((row, idx) => (
                    <Box key={`${row.name}-${idx}`} sx={{ px: 1.5, py: 1, borderRadius: '9px', background: alpha(SURFACE_TOP, 0.4), border: `1px solid ${alpha(NEON, 0.12)}`, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.86rem', color: TEXT_BODY }}>{row.name}</Typography>
                      {row.amount != null && row.amount !== '' && (
                        <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.78rem', color: NEON2, flexShrink: 0 }}>
                          RM {Number(row.amount).toFixed(2)}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Description */}
            {claim.description && (
              <Box>
                <Typography sx={{ fontSize: '0.65rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.8, px: 1.2 }}>
                  Description
                </Typography>
                <Box sx={{ px: 2, py: 1.5, borderRadius: '10px', borderLeft: `3px solid ${alpha(NEON, 0.4)}`, background: alpha(SURFACE_TOP, 0.4), minHeight: 80 }}>
                  <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', color: TEXT_BODY, lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
                    {claim.description}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Section>
        ) : null}

        {/* ══════════════════════════════════════════
            ROW 3 — RECEIPT DETAILS | PURCHASE RECEIPT
            2 columns, equal height
            ══════════════════════════════════════════ */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>

          {/* Receipt Details */}
          <Section title="Receipt Details" icon={ReceiptIcon} accent={NEON} delay={0.12} stretch>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
              <InfoRow icon={BusinessIcon}  label="Company Name"    value={claim.company} />
              <InfoRow
                icon={PersonIcon}
                label="Claim for"
                value={claim.full_name || claimantName}
              />
              <InfoRow
                icon={PersonIcon}
                label="Submitted by"
                value={claimantName}
                onClick={claimantId ? () => navigate(userDetailsPath(claimantId)) : undefined}
              />
              <InfoRow icon={CalendarIcon}  label="Purchase Date"   value={formatDateSafe(claim.purchase_payment_date || claim.payment_date, 'EEEE, MMMM dd, yyyy')} />
              <InfoRow icon={TagIcon}       label="Receipt Number"  value={claim.receipt_number} />
              <InfoRow icon={PaymentIcon}   label="Purchase Method" value={formatMethod(claim.purchase_payment_method)} color={NEON} />
            </Box>
          </Section>

          {/* Purchase Receipt image / PDF */}
          {hasPurchaseReceipt ? (
            <DocumentViewer
              title="Payment Receipt"
              receiptLabel={claim.receipt_number ? `Payment Receipt · ${claim.receipt_number}` : 'Payment Receipt'}
              receiptUrl={purchaseReceiptUrl}
              isPdf={purchaseReceiptIsPdf}
              accent={NEON}
              delay={0.14}
            />
          ) : (
            <EmptyReceiptPlaceholder title="Payment Receipt" accent={NEON} delay={0.14} />
          )}
        </Box>

        {/* ══════════════════════════════════════════
            ROW 4 — REIMBURSEMENT INFO | REIMBURSEMENT RECEIPT
            2 columns, equal height
            ══════════════════════════════════════════ */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>

          
          {/* Reimbursement Receipt image / PDF */}
          {hasReimbursementReceipt ? (
            <DocumentViewer
              title="Reimbursement Receipt"
              receiptLabel="Reimbursement Proof"
              receiptUrl={reimbursementReceiptUrl}
              isPdf={reimbursementReceiptIsPdf}
              accent={NEON2}
              delay={0.18}
            />
          ) : (
            <EmptyReceiptPlaceholder title="Reimbursement Receipt" accent={NEON2} delay={0.18} />
          )}
          
          {/* Reimbursement Information */}
          <Section title="Reimbursement Information" icon={PaymentIcon} accent={NEON2} delay={0.16} stretch>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
              <InfoRow icon={PaymentIcon}      label="Preferred Method"       value={formatMethod(claim.reimbursement_payment_method || claim.payment_method)} color={NEON2} />
              <InfoRow icon={AccountBalanceIcon} label="Account Number"       value={claim.payment_account} color={NEON2} />
              <InfoRow icon={CalendarIcon}     label="Reimbursement Date"     value={formatDateSafe(claim.reimbursement_payment_date || claim.updated_at, 'EEEE, MMMM dd, yyyy')} color={NEON2} />
              <InfoRow icon={ScheduleIcon}     label="Status"                 value={statusCfg.label} color={statusCfg.color} />
              <InfoRow icon={CheckCircleIcon}  label="Is Claimable"           value={claim.is_claimable ? 'Yes' : 'No'} color={claim.is_claimable ? NEON3 : CORAL} />
              <InfoRow icon={VerifiedIcon}     label="Is Paid"                value={claim.is_paid ? 'Paid' : 'Unpaid'} color={claim.is_paid ? NEON3 : AMBER} />
              {claim.payment_method === 'stripe' && (
                <InfoRow icon={PaymentIcon} label="Stripe (demo)" value={claim.stripe_payment_intent_id || claim.stripe_checkout_session_id || 'Confirmed'} color={NEON3} />
              )}
            </Box>

            <ClaimStripePay
              claim={claim}
              currentUser={currentUser}
              onPaid={() => fetchClaimDetails()}
            />

            {/* Rejection reason inside reimbursement section */}
            {claim.rejection_reason && (
              <Box sx={{ mt: 1.5, px: 0.5 }}>
                <Box sx={{
                  px: 2, py: 1.5, borderRadius: '10px',
                  borderLeft: `3px solid ${alpha(CORAL, 0.6)}`,
                  background: alpha(CORAL, 0.05),
                  border: `1px solid ${alpha(CORAL, 0.18)}`,
                }}>
                  <Typography sx={{ fontSize: '0.62rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: alpha(CORAL, 0.8), textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>
                    Rejection / Review Reason
                  </Typography>
                  <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.86rem', color: TEXT_BODY, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                    {claim.rejection_reason}
                  </Typography>
                </Box>
              </Box>
            )}
          </Section>

        </Box>

        {/* ══════════════════════════════════════════
            SYSTEM INFORMATION  (unchanged)
            ══════════════════════════════════════════ */}
        <SystemInformationSection
          createdBy={formatAuditUser(claim.record_creator) || claim.full_name}
          createdAt={formatSystemDateTime(claim.created_at)}
          updatedBy={formatAuditUser(claim.last_updater)}
          updatedAt={formatSystemDateTime(claim.updated_at)}
          delay={0.2}
          accentColor={NEON}
          surfaceUp={SURFACE_UP}
          surfaceMid={SURFACE_MID}
          textBody={TEXT_BODY}
          textMuted={TEXT_MUTED}
        />
      </Box>

      <ConfirmRejectDialog
        open={rejectDialogOpen}
        onClose={() => !rejectLoading && setRejectDialogOpen(false)}
        onConfirm={handleRejectClaim}
        title="Reject claim"
        itemName={claim?.item_bought || claim?.description || ''}
        confirmLabel="Reject claim"
        loading={rejectLoading}
        errorMessage={rejectError}
        count={1}
      />
    </Container>
  );
};

export default ClaimDetails;